import { groupBy } from 'lodash';
import { useTranslation } from 'react-i18next';

import { useDialogs } from '@work4all/components';

import { Entities } from '@work4all/models/lib/Enums/Entities.enum';
import { SdObjType } from '@work4all/models/lib/Enums/SdObjType.enum';

import { updateNonRelevantPositions } from '../components/utils';
import {
  ErpConversionGroupByType,
  ErpDialogConfirm,
  ErpObject,
  OnConfirmOptions,
  SelectedPosition,
  TargetEntityOptions,
} from '../types';

interface UseConversionSubmitProps {
  groupByType: ErpConversionGroupByType;
  positions: Record<number, SelectedPosition[]>;
  description: string;
  intoSingle: boolean;
  historicalItems: boolean;
  onConfirm: (
    confirm: ErpDialogConfirm[],
    targetEntity: OnConfirmOptions
  ) => void;
  setDisabled: (disabled: boolean) => void;
  data: ErpObject[];
  contractToDeliveryNote?: boolean;
  showShowSourceOptions: boolean;
  disableShowSourceOptions: boolean;
  freeze: boolean;
  close: boolean;
  refreshHeadAndClosingText: boolean;
  passThroughEnabled: boolean;
  targetEntity: Entities;
  targetEntityOptions?: TargetEntityOptions;
}

export const useConversionSubmit = (props: UseConversionSubmitProps) => {
  const {
    groupByType,
    positions,
    description,
    intoSingle,
    onConfirm,
    historicalItems,
    setDisabled,
    data,
    contractToDeliveryNote,
    showShowSourceOptions,
    disableShowSourceOptions,
    freeze,
    close,
    refreshHeadAndClosingText,
    passThroughEnabled,
    targetEntity,
    targetEntityOptions,
  } = props;

  const { t } = useTranslation();
  const dialogs = useDialogs();

  return async () => {
    // -- submit with ERP BP TYPE change
    // split to many suppliers and call for each supplier separately
    if (groupByType !== 'NONE') {
      let positionBySupplier: Record<number, SelectedPosition[]> = {};

      if (['SUPPLIER', 'CONTRACT'].includes(groupByType)) {
        const contractToSupplierEntries = Object.entries(positions).flatMap(
          (x) => x[1]
        );
        const grouped = groupBy(
          contractToSupplierEntries,
          'position.supplier.id'
        );

        positionBySupplier = Object.entries(grouped).reduce(
          (prev, [key, value]) => {
            const newKey = ['undefined', '0'].includes(key) ? '0' : key;
            const newValue = { ...prev, [newKey]: value };
            return newValue;
          },
          {}
        );
      } else {
        positionBySupplier = positions;
      }

      const getArticleDetails = (groupedBySupplier: SelectedPosition[]) => {
        const articleDetails = groupedBySupplier.map((x) => ({
          articleCode: x.position.articleId,
          newAmount: x.newAmount,
          newDiscount: x.newDiscount,
          newSinglePrice: x.newSinglePrice ? x.newSinglePrice : undefined,
          newUnit: x.newUnit,
          supplierCode: x.position.supplier?.id,
        }));

        return [
          {
            targetNewName: description,
            articleDetails,
            cloneOrderConfirmationPositions: passThroughEnabled,
            options: {
              createHistoryPosition: false,
              createMultipleObjects: false,
              refreshHeadAndClosingText,
            },
            newTargetSdObjType: SdObjType.LIEFERANT,
          },
        ];
      };

      // Operate over each supplier
      const toModify = passThroughEnabled
        ? Object.entries(positionBySupplier)
            .filter(
              (x) => x[0] !== '0' && x[0] !== 'undefined' && !Number.isNaN(x[0])
            )
            .filter((x) => x[1].length > 0 && x[1].some((y) => y.selected))
            .map(([newTargetSdObjMemberCode, groupedBySupplier]) => {
              const positionDetails = [
                // map items by specific supplier
                ...groupedBySupplier.flatMap((x) => {
                  if (x.childsOriginalData) {
                    return x.childsOriginalData.map((childPosition) => ({
                      positionCode:
                        childPosition.position.originalId ||
                        childPosition.position.id,
                      doNotConvert: !childPosition.selected,
                    }));
                  }

                  return [
                    {
                      positionCode: x.position.originalId || x.position.id,
                      doNotConvert: !x.selected,
                    },
                  ];
                }),
              ];

              return {
                targetNewName: description,
                positionDetails,
                cloneOrderConfirmationPositions: passThroughEnabled,
                options: {
                  createHistoryPosition: false,
                  createMultipleObjects: false,
                  refreshHeadAndClosingText,
                },
                newTargetSdObjType: SdObjType.LIEFERANT,
                newTargetSdObjMemberCode: parseInt(newTargetSdObjMemberCode),
              };
            })
        : getArticleDetails(
            Object.entries(positionBySupplier)
              .filter(
                (x) =>
                  x[0] !== '0' && x[0] !== 'undefined' && !Number.isNaN(x[0])
              )
              .filter((x) => x[1].length > 0 && x[1].some((y) => y.selected))
              .flatMap((x) => x[1])
          );

      const confirmed = await dialogs.confirm({
        title: t('COMMON.ERP.CONVERSION_CONFIRM_TITLE'),
        description:
          toModify.length > 1
            ? t(
                'COMMON.ERP.CONVERSION_CONFIRM_MESSAGE_CONTRACT_TO_ORDER_MULTI',
                { count: toModify.length }
              )
            : t('COMMON.ERP.CONVERSION_CONFIRM_MESSAGE_CONTRACT_TO_ORDER'),
      });

      if (confirmed) onConfirm(toModify, { targetEntity });
      else setDisabled(false);
    } else {
      setDisabled(true);

      const selectedPositions = Object.values(positions).flatMap((x) => x);
      const positionsToConvert = contractToDeliveryNote
        ? updateNonRelevantPositions(selectedPositions)
        : selectedPositions.filter((x) => !isChanged(x) || !x.selected);

      const submitData = {
        targetNewName: description,
        positionDetails: positionsToConvert.map((x) => ({
          positionCode: x.position.id,
          doNotConvert: !x.selected,
          newAmount: x.newAmount,
          newDiscount: x.newDiscount,
          newSinglePrice: x.newSinglePrice,
        })),
        invoiceAsCreditNote: targetEntityOptions?.invoiceAsCreditNote,
        options: {
          createHistoryPosition: historicalItems,
          createMultipleObjects: !intoSingle,
          refreshHeadAndClosingText,
          ...(showShowSourceOptions &&
            !disableShowSourceOptions && {
              closeSourceBzObject: close,
            }),
          ...(showShowSourceOptions &&
            !disableShowSourceOptions && {
              freezeSourceBzObject: freeze,
            }),
        },
      };

      if (data.length === 1 || intoSingle) {
        onConfirm([submitData], { targetEntity });
        return;
      }

      const confirmed = await dialogs.confirm({
        title: t('COMMON.ERP.CONVERSION_CONFIRM_TITLE'),
        description: t('COMMON.ERP.CONVERSION_CONFIRM_MESSAGE'),
      });
      if (confirmed) onConfirm([submitData], { targetEntity });
      else setDisabled(false);
    }
  };
};

export const isChanged = (pos: SelectedPosition) => {
  return (
    pos.newAmount === pos.oldAmount &&
    pos.newDiscount === pos.oldDiscount &&
    pos.newSinglePrice === pos.oldSinglePrice
  );
};
